/**
 * CampaignListPanel Component
 * Left sidebar with campaign list, search, and filters
 */

import React, { useState, useMemo } from 'react';
import { Search, Filter, ChevronDown, TrendingUp, TrendingDown } from 'lucide-react';
import { useCommandCenter } from '../../contexts/CommandCenterContext';
import {
    getCampaignDisplayData,
    getCampaignEndStatus,
    calculateBudgetPacing,
    calculateObjectiveAwareTrend,
    PACING_STATUS,
    extractMetricValue,
    getObjectiveConfig,
    normalizeObjective
} from '../../utils/objectiveHelpers';
import { formatCurrency } from '../../utils/helpers';

const FILTER_OPTIONS = [
    { value: 'all', label: 'All Campaigns' },
    { value: 'active', label: 'Active' },
    { value: 'paused', label: 'Paused' },
    { value: 'ending_soon', label: 'Ending Soon' },
    { value: 'overspending', label: 'Overspending' },
    { value: 'underspending', label: 'Underspending' }
];

const CampaignListPanel = () => {
    const {
        campaigns,
        selectedCampaignId,
        selectCampaign,
        previousPeriodData,
        isLoading
    } = useCommandCenter();

    const [searchQuery, setSearchQuery] = useState('');
    const [filterValue, setFilterValue] = useState('all');
    const [showFilterDropdown, setShowFilterDropdown] = useState(false);

    // Filter and search campaigns
    const filteredCampaigns = useMemo(() => {
        let result = campaigns;

        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            result = result.filter(c =>
                c.name?.toLowerCase().includes(query) ||
                c.objective?.toLowerCase().includes(query)
            );
        }

        // Apply status filter
        switch (filterValue) {
            case 'active':
                result = result.filter(c =>
                    c.status?.toUpperCase() === 'ACTIVE'
                );
                break;
            case 'paused':
                result = result.filter(c =>
                    c.status?.toUpperCase() === 'PAUSED'
                );
                break;
            case 'ending_soon':
                result = result.filter(c => {
                    const endStatus = getCampaignEndStatus(c);
                    return endStatus.isUrgent && !endStatus.isEnded;
                });
                break;
            case 'overspending':
                result = result.filter(c => {
                    const pacing = calculateBudgetPacing(c);
                    return pacing.status === PACING_STATUS.OVERPACING;
                });
                break;
            case 'underspending':
                result = result.filter(c => {
                    const pacing = calculateBudgetPacing(c);
                    return pacing.status === PACING_STATUS.UNDERPACING;
                });
                break;
            default:
                break;
        }

        return result;
    }, [campaigns, searchQuery, filterValue]);

    const selectedFilterLabel = FILTER_OPTIONS.find(o => o.value === filterValue)?.label || 'All';

    if (isLoading) {
        return (
            <div style={{
                width: '320px',
                minWidth: '320px',
                borderRight: '1px solid var(--color-border)',
                background: 'var(--color-surface)',
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
            }}>
                <div style={{ padding: 'var(--space-4)' }}>
                    <div style={{
                        height: '36px',
                        background: 'var(--color-background)',
                        borderRadius: 'var(--radius-md)',
                        marginBottom: 'var(--space-3)',
                        animation: 'pulse 2s infinite'
                    }} />
                    <div style={{
                        height: '36px',
                        background: 'var(--color-background)',
                        borderRadius: 'var(--radius-md)',
                        animation: 'pulse 2s infinite'
                    }} />
                </div>
                <div style={{ padding: 'var(--space-4)', flex: 1 }}>
                    {[1, 2, 3, 4].map(i => (
                        <div
                            key={i}
                            style={{
                                height: '100px',
                                background: 'var(--color-background)',
                                borderRadius: 'var(--radius-md)',
                                marginBottom: 'var(--space-3)',
                                animation: 'pulse 2s infinite'
                            }}
                        />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div style={{
            width: '320px',
            minWidth: '320px',
            borderRight: '1px solid var(--color-border)',
            background: 'var(--color-surface)',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden'
        }}>
            {/* Search and Filter Header */}
            <div style={{
                padding: 'var(--space-4)',
                borderBottom: '1px solid var(--color-border)'
            }}>
                {/* Search Input */}
                <div style={{
                    position: 'relative',
                    marginBottom: 'var(--space-3)'
                }}>
                    <Search
                        size={16}
                        style={{
                            position: 'absolute',
                            left: 'var(--space-3)',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: 'var(--color-text-tertiary)'
                        }}
                    />
                    <input
                        type="text"
                        placeholder="Search campaigns..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                            width: '100%',
                            padding: 'var(--space-2) var(--space-3) var(--space-2) var(--space-8)',
                            background: 'var(--color-background)',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-md)',
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-text-primary)',
                            outline: 'none',
                            transition: 'border-color var(--transition-fast)'
                        }}
                        onFocus={(e) => {
                            e.target.style.borderColor = 'var(--color-primary)';
                        }}
                        onBlur={(e) => {
                            e.target.style.borderColor = 'var(--color-border)';
                        }}
                    />
                </div>

                {/* Filter Dropdown */}
                <div style={{ position: 'relative' }}>
                    <button
                        onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                        style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: 'var(--space-2) var(--space-3)',
                            background: 'var(--color-background)',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-md)',
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-text-primary)',
                            cursor: 'pointer'
                        }}
                    >
                        <span style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)'
                        }}>
                            <Filter size={14} />
                            {selectedFilterLabel}
                        </span>
                        <ChevronDown
                            size={14}
                            style={{
                                transform: showFilterDropdown ? 'rotate(180deg)' : 'none',
                                transition: 'transform var(--transition-fast)'
                            }}
                        />
                    </button>

                    {showFilterDropdown && (
                        <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            marginTop: 'var(--space-1)',
                            background: 'var(--color-surface)',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-md)',
                            boxShadow: 'var(--shadow-lg)',
                            zIndex: 10,
                            overflow: 'hidden'
                        }}>
                            {FILTER_OPTIONS.map(option => (
                                <button
                                    key={option.value}
                                    onClick={() => {
                                        setFilterValue(option.value);
                                        setShowFilterDropdown(false);
                                    }}
                                    style={{
                                        width: '100%',
                                        padding: 'var(--space-2) var(--space-3)',
                                        background: filterValue === option.value
                                            ? 'var(--color-primary-alpha)'
                                            : 'transparent',
                                        border: 'none',
                                        fontSize: 'var(--font-size-sm)',
                                        color: filterValue === option.value
                                            ? 'var(--color-primary)'
                                            : 'var(--color-text-primary)',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        transition: 'background var(--transition-fast)'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (filterValue !== option.value) {
                                            e.target.style.background = 'var(--color-surface-hover)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (filterValue !== option.value) {
                                            e.target.style.background = 'transparent';
                                        }
                                    }}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Campaign List */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: 'var(--space-2)'
            }}>
                {filteredCampaigns.length === 0 ? (
                    <div style={{
                        padding: 'var(--space-8) var(--space-4)',
                        textAlign: 'center',
                        color: 'var(--color-text-tertiary)'
                    }}>
                        <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
                            {searchQuery || filterValue !== 'all'
                                ? 'No campaigns match your filters'
                                : 'No campaigns found'
                            }
                        </p>
                    </div>
                ) : (
                    filteredCampaigns.map(campaign => (
                        <CampaignCard
                            key={campaign.id}
                            campaign={campaign}
                            isSelected={selectedCampaignId === campaign.id}
                            onSelect={() => selectCampaign(campaign.id)}
                            previousMetrics={previousPeriodData[campaign.id]}
                        />
                    ))
                )}
            </div>

            {/* Campaign count footer */}
            <div style={{
                padding: 'var(--space-2) var(--space-4)',
                borderTop: '1px solid var(--color-border)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-tertiary)',
                textAlign: 'center'
            }}>
                {filteredCampaigns.length} of {campaigns.length} campaigns
            </div>
        </div>
    );
};

// Individual Campaign Card
const CampaignCard = ({ campaign, isSelected, onSelect, previousMetrics }) => {
    const displayData = getCampaignDisplayData(campaign);
    const endStatus = getCampaignEndStatus(campaign);
    const pacing = calculateBudgetPacing(campaign);

    // Calculate trend for primary metric
    const objective = normalizeObjective(campaign.objective);
    const config = getObjectiveConfig(objective);
    const currentValue = extractMetricValue(
        campaign.currentMetrics || campaign.insights?.data?.[0],
        config.primaryKPI
    );
    const previousValue = previousMetrics
        ? extractMetricValue(previousMetrics, config.primaryKPI)
        : null;

    const trend = previousValue !== null
        ? calculateObjectiveAwareTrend(currentValue, previousValue, objective, config.primaryKPI)
        : null;

    const statusColors = {
        ACTIVE: 'var(--color-success)',
        PAUSED: 'var(--color-warning)',
        DELETED: 'var(--color-error)',
        ARCHIVED: 'var(--color-text-tertiary)'
    };

    const statusColor = statusColors[campaign.status?.toUpperCase()] || 'var(--color-text-secondary)';

    return (
        <button
            onClick={onSelect}
            style={{
                width: '100%',
                padding: 'var(--space-3)',
                marginBottom: 'var(--space-2)',
                background: isSelected
                    ? 'var(--color-primary-alpha)'
                    : 'var(--color-background)',
                border: isSelected
                    ? '1px solid var(--color-primary)'
                    : '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all var(--transition-fast)'
            }}
            onMouseEnter={(e) => {
                if (!isSelected) {
                    e.currentTarget.style.borderColor = 'var(--color-primary-light)';
                    e.currentTarget.style.background = 'var(--color-surface-hover)';
                }
            }}
            onMouseLeave={(e) => {
                if (!isSelected) {
                    e.currentTarget.style.borderColor = 'var(--color-border)';
                    e.currentTarget.style.background = 'var(--color-background)';
                }
            }}
        >
            {/* Campaign Name and Status */}
            <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                marginBottom: 'var(--space-2)'
            }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        marginBottom: '2px'
                    }}>
                        <span style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: statusColor,
                            flexShrink: 0
                        }} />
                        <span style={{
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 600,
                            color: 'var(--color-text-primary)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            {campaign.name}
                        </span>
                    </div>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-tertiary)'
                    }}>
                        <span>{displayData.objectiveIcon}</span>
                        <span>{displayData.objectiveLabel}</span>
                    </div>
                </div>
            </div>

            {/* Metrics Row */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--space-2)'
            }}>
                <span style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-text-secondary)'
                }}>
                    {displayData.spend}
                </span>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)'
                }}>
                    <span style={{
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 600,
                        color: 'var(--color-text-primary)'
                    }}>
                        {displayData.primaryMetric}
                    </span>
                    {trend && trend.direction !== 'neutral' && (
                        <span style={{
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: 'var(--font-size-xs)',
                            color: trend.color,
                            fontWeight: 500
                        }}>
                            {trend.direction === 'up' ? (
                                <TrendingUp size={12} />
                            ) : (
                                <TrendingDown size={12} />
                            )}
                            {trend.percentage.toFixed(0)}%
                        </span>
                    )}
                </div>
            </div>

            {/* Footer Row - End Date & Pacing */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: 'var(--font-size-xs)'
            }}>
                {/* End Date */}
                <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-1)',
                    color: endStatus.isUrgent
                        ? 'var(--color-warning)'
                        : 'var(--color-text-tertiary)'
                }}>
                    <span>{endStatus.icon}</span>
                    <span>{endStatus.display}</span>
                </span>

                {/* Budget Pacing */}
                {pacing.status !== 'unknown' && pacing.percentage > 0 && (
                    <span style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-1)',
                        color: pacing.status === PACING_STATUS.OVERPACING
                            ? 'var(--color-error)'
                            : pacing.status === PACING_STATUS.UNDERPACING
                                ? 'var(--color-warning)'
                                : 'var(--color-success)'
                    }}>
                        Budget: {pacing.percentage.toFixed(0)}%
                    </span>
                )}
            </div>
        </button>
    );
};

export default CampaignListPanel;
